import { VIRTUAL_ASSISTANT_LANGUAGE_LIST } from "@constants/languageConstants";
import {
  AvailableOption,
  EyeType,
  AvailableEyeConfigurationOptions,
  TestLanguage,
  AvailableTestSpecificConfigurationOptions,
  TestUIConfiguration,
} from "types/tests";
import { FEATURE_VERSION_MAP, VIRTUAL_ASSISTANT_LANGUAGE_VERSION_MAP } from "./versionConstants";
import { HeadsetVersion } from "types/productTypes";

export const DEFAULT_TEST_UI_CONFIGURATION: TestUIConfiguration = {
  showProgressBar: true,
  instructions: null,
  isTestOtherEyeOptionPermitted: false,
  isTurnOffDeviceOptionPermitted: false,
};

const STANDARD_EYE_CONFIGURATION_OPTIONS_BINOCULAR: AvailableOption<EyeType> = {
  configurationFieldName: "eyeType",
  validSinceVersion: null,
  choices: [{ validSinceVersion: null, value: EyeType.BothEye }],
  isMandatory: true,
};

const STANDARD_EYE_CONFIGURATION_OPTIONS_BINOCULAR_MONOCULAR: AvailableOption<EyeType> = {
  configurationFieldName: "eyeType",
  validSinceVersion: null,
  choices: [
    { validSinceVersion: null, value: EyeType.BothEye },
    { validSinceVersion: null, value: EyeType.RightEye },
    { validSinceVersion: null, value: EyeType.LeftEye },
  ],
  isMandatory: true,
};

const STANDARD_EYE_CONFIGURATION_OPTIONS_BINOCULAR_MONOCULAR_BACK2BACK: AvailableOption<EyeType> = {
  configurationFieldName: "eyeType",
  validSinceVersion: null,
  choices: [
    { validSinceVersion: null, value: EyeType.BothEye },
    { validSinceVersion: null, value: EyeType.RightEye },
    { validSinceVersion: null, value: EyeType.LeftEye },
    { validSinceVersion: HeadsetVersion.ver_2_8_5, value: EyeType.RightThenLeft },
  ],
  isMandatory: true,
};

const STANDARD_EYE_CONFIGURATION_OPTIONS_MONOCULAR_BACK2BACK: AvailableOption<EyeType> = {
  configurationFieldName: "eyeType",
  validSinceVersion: null,
  choices: [
    { validSinceVersion: null, value: EyeType.RightEye },
    { validSinceVersion: null, value: EyeType.LeftEye },
    { validSinceVersion: HeadsetVersion.ver_2_8_5, value: EyeType.RightThenLeft },
  ],
  isMandatory: true,
};

const STANDARD_EYE_CONFIGURATION_OPTIONS_MONOCULAR: AvailableOption<EyeType> = {
  configurationFieldName: "eyeType",
  validSinceVersion: null,
  choices: [
    { validSinceVersion: null, value: EyeType.RightEye },
    { validSinceVersion: null, value: EyeType.LeftEye },
  ],
  isMandatory: true,
};

export const CALIBRATION_TOGGLE_OPTION: AvailableOption<boolean> = {
  configurationFieldName: "isCalibrationNeeded",
  validSinceVersion: null,
  choices: null,
};

export const CALIBRATION_TOGGLE_OPTION_NEW_APK: AvailableOption<boolean> = {
  configurationFieldName: "isCalibrationNeeded",
  validSinceVersion: FEATURE_VERSION_MAP["extraTestCalibrationToggle"],
  choices: null,
};

export const AVAILABLE_EYE_OPTIONS_BINOCULAR: AvailableEyeConfigurationOptions = {
  eyeTypes: STANDARD_EYE_CONFIGURATION_OPTIONS_BINOCULAR,
  pauseOnFocusLostOption: null,
  toggleCalibrationOption: null,
  toggleTwoScreenFixationOption: null,
};

export const AVAILABLE_EYE_OPTIONS_BINOCULAR_MONOCULAR: AvailableEyeConfigurationOptions = {
  eyeTypes: STANDARD_EYE_CONFIGURATION_OPTIONS_BINOCULAR_MONOCULAR,
  pauseOnFocusLostOption: null,
  toggleCalibrationOption: null,
  toggleTwoScreenFixationOption: null,
};

export const AVAILABLE_EYE_OPTIONS_BINOCULAR_MONOCULAR_BACK2BACK: AvailableEyeConfigurationOptions =
  {
    eyeTypes: STANDARD_EYE_CONFIGURATION_OPTIONS_BINOCULAR_MONOCULAR_BACK2BACK,
    pauseOnFocusLostOption: null,
    toggleCalibrationOption: null,
    toggleTwoScreenFixationOption: null,
  };

export const AVAILABLE_EYE_OPTIONS_MONOCULAR_BACK2BACK: AvailableEyeConfigurationOptions = {
  eyeTypes: STANDARD_EYE_CONFIGURATION_OPTIONS_MONOCULAR_BACK2BACK,
  pauseOnFocusLostOption: null,
  toggleCalibrationOption: null,
  toggleTwoScreenFixationOption: null,
};

export const AVAILABLE_EYE_OPTIONS_MONOCULAR: AvailableEyeConfigurationOptions = {
  eyeTypes: STANDARD_EYE_CONFIGURATION_OPTIONS_MONOCULAR,
  pauseOnFocusLostOption: null,
  toggleCalibrationOption: null,
  toggleTwoScreenFixationOption: null,
};

export const AVAILABLE_VIRTUAL_ASSISTANT_OPTIONS: AvailableOption<TestLanguage> = {
  configurationFieldName: "virtualAssistanceConfig",
  validSinceVersion: FEATURE_VERSION_MAP["minVirtualAssistant"],
  choices: VIRTUAL_ASSISTANT_LANGUAGE_LIST,
};

// Tests whose virtual assistant only ships English instruction videos (OKN, NPC,
// anti-saccade). Offering the full language list would let users pick a language
// with no corresponding video. NOT_SPECIFIED is kept as the toggle-off value the
// segmented-control selector writes when the assistant is disabled.
export const AVAILABLE_VIRTUAL_ASSISTANT_OPTIONS_ENGLISH_ONLY: AvailableOption<TestLanguage> = {
  configurationFieldName: "virtualAssistanceConfig",
  validSinceVersion: FEATURE_VERSION_MAP["minVirtualAssistant"],
  choices: [
    {
      value: TestLanguage.NOT_SPECIFIED,
      validSinceVersion: VIRTUAL_ASSISTANT_LANGUAGE_VERSION_MAP[TestLanguage.NOT_SPECIFIED],
    },
    {
      value: TestLanguage.English,
      validSinceVersion: VIRTUAL_ASSISTANT_LANGUAGE_VERSION_MAP[TestLanguage.English],
    },
  ],
};

export const DUMMY_AVAILABLE_TEST_SPECIFIC_CONFIGURATION_OPTIONS: AvailableTestSpecificConfigurationOptions =
  {
    cptConfigModes: null,
    dyscalculiaConfigModes: null,
    fdtBehaviourTypes: null,
    kineticDbColors: null,
    vfaBehaviourOptions: null,
    toggleBlindSpotMonitoring: null,
    toggleFovealThresholdTesting: null,
    toggleBlindSpotTesting: null,
    fixationSizeOptions: null,
    stimuliSizeOptions: null,
    kineticPatterns: null,
    testTypeCircleAngleOptions: null,
    numberOfStimuliStartsOptions: null,
    plateNumberOptions: null,
    toggleShowTutorial: null,
  };
